"use client";
import { createContext, useContext, useEffect, useRef, ReactNode, useState, useCallback } from "react";
import { setActiveSocketId } from "@/lib/api-helpers";
import { io, Socket } from "socket.io-client";
import { fetchWithAuth } from "@/lib/api-helpers";
import { eventBus } from "@/services-frontend/comment/eventBus";

// ─────────────────────────────────────────────────────────────────────────────
// SOCKET CONTEXT
//
// Manages a single persistent socket connection for the entire app.
//
// Rooms this socket participates in:
//   1. Personal room  → identified by user email (joined on connect)
//      Used for: MENTION, ASSIGNED, JOIN_REQUEST_DECISION — 1:1 targeted events
//
//   2. Workspace room → identified by ws:{workspaceId} (joined via /api/socket/join)
//      Used for: PUBLIC_PAGE, NEW_COMMENT — broadcast events to all workspace members
//      Join flow: Browser calls /api/socket/join → Next.js verifies membership →
//                 Next.js calls Socket Server POST /internal/force-join →
//                 Socket Server does socket.join(`ws:{workspaceId}`)
//
// Event flow:
//   Next.js API → emitServerEvent("server-ping", { targets, actionType }) →
//   Socket Server → io.to(email).emit("client-ping") →
//   Browser → notificationSocketListner.tsx → handleClientPing()
// ─────────────────────────────────────────────────────────────────────────────

interface SocketContextType {
  socket: Socket | null;
  // Call this when the user navigates into a workspace.
  // Makes an API call to /api/socket/join which verifies membership
  // then tells the Socket Server to add this socket to ws:{workspaceId}
  joinWorkspace: (workspaceId: string) => Promise<void>;
  // Call this when the user leaves a workspace (navigates away).
  leaveWorkspace: (workspaceId: string) => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  joinWorkspace: async () => {},
  leaveWorkspace: () => {},
});

interface SocketProviderProps {
  // NOTE: In providers.tsx, userId is set to user.email — so this IS the email.
  // The socket joins a personal room named by this value.
  userId: string;
  children: ReactNode;
}

export const SocketProvider = ({ userId, children }: SocketProviderProps) => {
  const socketRef = useRef<Socket | null>(null);
  const [socketInstance, setSocketInstance] = useState<Socket | null>(null);

  // Tracks which workspace room this socket is currently in.
  // Used by the reconnect handler to auto-rejoin after a network blip —
  // Socket.IO drops all room memberships on disconnect, so we must re-join.
  const activeWorkspaceIdRef = useRef<string | null>(null);

  // Always-latest ref to joinWorkspace — lets the socket useEffect call it
  // without adding joinWorkspace to the effect's dependency array (which would
  // tear down and recreate the socket connection on every render).
  const joinWorkspaceFnRef = useRef<((id: string) => Promise<void>) | null>(null);

  useEffect(() => {
    if (!userId || userId === "guest") {
      console.log(`[SocketContext] ⏭ Skipping socket init — no authenticated user`);
      return;
    }

    let cancelled = false;
    let socket: Socket | null = null;

    // CRIT-02 — fetch a short-lived signed token before opening the socket.
    // The backend authenticates the caller via NextAuth cookie, then HMAC-signs
    // an { email, iat, exp } payload that the socket server will verify.
    //
    // We refetch the token automatically before reconnect attempts so a long
    // disconnect (token expired) still produces a usable handshake.
    async function fetchSocketToken(): Promise<string | null> {
      try {
        const res = await fetchWithAuth("/api/socket/token", { method: "POST" });
        if (!res.ok) {
          console.warn(`[SocketContext] token fetch failed: ${res.status}`);
          return null;
        }
        const json = await res.json();
        return typeof json?.token === "string" ? json.token : null;
      } catch (e) {
        console.error("[SocketContext] token fetch error:", e);
        return null;
      }
    }

    (async () => {
      const initialToken = await fetchSocketToken();
      if (cancelled) return;
      if (!initialToken) {
        console.warn("[SocketContext] no socket token; skipping connect");
        return;
      }

      // No hardcoded production fallback URL — connecting a session token to
      // a server we don't control via a stale default is worse than staying
      // offline. Require the env var explicitly.
      const socketUrl = process.env.NEXT_PUBLIC_SOCKET_SERVER_URL;
      if (!socketUrl) {
        console.warn(
          "[SocketContext] NEXT_PUBLIC_SOCKET_SERVER_URL not set; skipping connect",
        );
        return;
      }

      console.log(
        `\n[SocketContext] 🔌 Initializing socket connection`,
        `\n  socketServerUrl : ${socketUrl}`,
        `\n  userId (email)  : ${userId}`,
      );

      socket = io(socketUrl, {
        // Drop polling — websocket-only narrows CSRF surface.
        transports: ["websocket"],
        withCredentials: true,
        reconnection: true,
        // Never give up: the old 5-attempt cap meant a laptop waking from
        // sleep could leave the tab permanently offline until a reload.
        // Capped exponential backoff keeps retry pressure low.
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        auth: { token: initialToken },
      });

      socket.on("connect", () => {
        // Publish this tab's id so API writes can be stamped with their origin.
        setActiveSocketId(socket?.id ?? null);
        console.log(
          `\n[SocketContext] ✅ Socket connected`,
          `\n  socketId       : ${socket?.id}`,
          `\n  userId (email) : ${userId}`,
        );
        // The socket server now derives the personal room from the verified
        // token, so a `join-room` event is technically not required. We still
        // emit it (no-op handler) for backward compatibility with any code
        // path that observes "join-room sent" as a readiness signal.
        socket?.emit("join-room", {});
        const pendingWsId = activeWorkspaceIdRef.current;
        if (pendingWsId) {
          joinWorkspaceFnRef.current?.(pendingWsId);
        }
      });

      socket.on("disconnect", (reason) => {
        setActiveSocketId(null);
        console.log(`[SocketContext] ❌ Socket disconnected (reason: ${reason})`);
      });

      // Refresh the auth token before every reconnect attempt so an expired
      // token doesn't permanently lock us out.
      socket.io.on("reconnect_attempt", async () => {
        const fresh = await fetchSocketToken();
        if (fresh && socket) {
          (socket as any).auth = { token: fresh };
        }
      });

      socket.on("connect_error", (err) => {
        console.error("[SocketContext] Connection error:", err);
      });

      socket.on("reconnect", () => {
        socket?.emit("join-room", {});
        const wsId = activeWorkspaceIdRef.current;
        if (wsId) {
          joinWorkspaceFnRef.current?.(wsId);
        }
        eventBus.emit("socket:reconnected", {});
      });

      socketRef.current = socket;
      setSocketInstance(socket);
    })();

    return () => {
      cancelled = true;
      if (socket) {
        socket.disconnect();
      }
      socketRef.current = null;
      setSocketInstance(null);
    };
  }, [userId]);

  // ─── JOIN WORKSPACE ROOM ────────────────────────────────────────────────────
  // Calls Next.js /api/socket/join which:
  //   1. Verifies the user is a member of the workspace (MongoDB check)
  //   2. Makes an HTTP call to Socket Server POST /internal/force-join
  //   3. Socket Server does: io.sockets.sockets.get(socketId).join(`ws:{workspaceId}`)
  // After this, the user will receive broadcast events via io.to(`ws:{workspaceId}`)
  const joinWorkspace = useCallback(async (workspaceId: string) => {
    // Always record the active workspace BEFORE any early return — this lets
    // the connect/reconnect handler auto-fire joinWorkspace once the socket
    // is actually ready, even if the first call landed before connection.
    activeWorkspaceIdRef.current = workspaceId;

    const socket = socketRef.current;
    if (!socket?.connected) {
      console.warn(
        `[SocketContext] ⚠ joinWorkspace queued — socket not connected yet, will fire on connect`,
        `\n  workspaceId : ${workspaceId}`,
      );
      return;
    }

    console.log(
      `\n[SocketContext] 🏢 joinWorkspace called`,
      `\n  workspaceId : ${workspaceId}`,
      `\n  socketId    : ${socket.id}`,
      `\n  user        : ${userId}`,
    );

    try {
      // Call the backend which verifies membership before joining.
      // fetchWithAuth → resolveUrl rewrites /api/socket/join to ${API_URL}/api/socket/join.
      // This is the security gate — the Socket Server trusts what the backend tells it.
      //
      // Retry on 502: the socket server returns 502 when our socketId can't
      // be found (the most common cause is the socket reconnected between
      // when we read socket.id here and when the backend's force-join call
      // hit the server). Wait a beat and retry with the now-current id.
      let attempt = 0;
      const maxAttempts = 3;
      let res: Response | undefined;
      while (attempt < maxAttempts) {
        const currentSocket = socketRef.current;
        if (!currentSocket?.connected) {
          console.warn(`[SocketContext] joinWorkspace retry skipped — socket dropped`);
          return;
        }
        res = await fetchWithAuth("/api/socket/join", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ workspaceId, socketId: currentSocket.id }),
        });
        if (res.ok) break;
        if (res.status !== 502) break; // only retry on stale-socket errors
        attempt += 1;
        await new Promise((r) => setTimeout(r, 200 * attempt));
      }
      if (!res) return;

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.warn(
          `[SocketContext] ❌ joinWorkspace failed`,
          `\n  workspaceId : ${workspaceId}`,
          `\n  status      : ${res.status}`,
          `\n  attempts    : ${attempt}`,
          `\n  error       : ${err?.error || err?.message || "unknown"}`,
        );
        return;
      }

      const result = await res.json();
      console.log(
        `[SocketContext] ✅ joinWorkspace success`,
        `\n  workspaceId : ${workspaceId}`,
        `\n  rooms       : ${JSON.stringify(result.rooms || [])}`,
      );
    } catch (err) {
      console.error(`[SocketContext] 💥 joinWorkspace error:`, err);
    }
  }, [userId]);

  // Keep the ref pointing at the latest joinWorkspace on every render.
  // The reconnect handler inside the socket useEffect closes over this ref (not
  // the function itself), so it always calls the current version without needing
  // joinWorkspace in the socket effect's dependency array.
  joinWorkspaceFnRef.current = joinWorkspace;

  // ─── LEAVE WORKSPACE ROOM ───────────────────────────────────────────────────
  // Socket.IO automatically removes from rooms on disconnect.
  // This handles graceful leave when switching workspaces without disconnecting.
  const leaveWorkspace = useCallback((workspaceId: string) => {
    // Clear tracked workspace BEFORE checking connection — otherwise a
    // disconnected leave (e.g. flaky network at the moment we navigate
    // away) would skip the ref clear, and the next reconnect handler
    // would happily re-join the workspace the user has already left.
    // Only clear if the ref actually matches the workspace being left,
    // so a fast workspace switch (joinWorkspace(B) before leaveWorkspace(A))
    // doesn't clobber the new tracking.
    if (activeWorkspaceIdRef.current === workspaceId) {
      activeWorkspaceIdRef.current = null;
    }

    const socket = socketRef.current;
    if (!socket?.connected) return;

    console.log(
      `\n[SocketContext] 🚪 leaveWorkspace`,
      `\n  workspaceId : ${workspaceId}`,
      `\n  socketId    : ${socket.id}`,
    );

    // Tell the socket server to remove this socket from the workspace room
    socket.emit("leave-workspace", { workspaceId });
  }, []);

  return (
    <SocketContext.Provider value={{ socket: socketInstance, joinWorkspace, leaveWorkspace }}>
      {children}
    </SocketContext.Provider>
  );
};

export function useSocketContext() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocketContext must be used within a SocketProvider");
  }
  return context;
}
